import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Compass, Heart } from 'lucide-react';
import PropertyCard from './PropertyCard';
import BudgetPill, { readBudget } from './BudgetPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem('lh_session_id');
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('lh_session_id', sid);
    }
    return sid;
  } catch {
    return '';
  }
};

/**
 * "Other homes you might love" rail at the bottom of /property/:id.
 *
 * Calls the same `/for-your-taste` endpoint as the listings row, but with
 * `like_property_id` so candidates sharing this property's county / style
 * get a similarity bonus. The visitor's personal taste is layered on top
 * when they have one.
 *
 * Reasoning headlines:
 *   personalised → "More homes that match your taste"
 *   similar      → "Other homes like this one"
 *   editorial    → never reached here (we always have a seed)
 */
const SimilarHomesRail = ({ propertyId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!propertyId) return undefined;
    let cancelled = false;
    const load = () => {
      const sid = getSessionId();
      const ub = readBudget();
      const params = {
        session_id: sid,
        limit: 4,
        like_property_id: propertyId,
        exclude_id: propertyId,
      };
      if (ub) {
        params.budget_min = ub.min;
        params.budget_max = ub.max;
      }
      axios
        .get(`${BACKEND_URL}/api/engagement/for-your-taste`, { params })
        .then((r) => !cancelled && setData(r.data))
        .catch(() => !cancelled && setData(null));
    };
    load();
    const handler = () => load();
    window.addEventListener('lh:reaction-recorded', handler);
    window.addEventListener('lh:budget-changed', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('lh:reaction-recorded', handler);
      window.removeEventListener('lh:budget-changed', handler);
    };
  }, [propertyId]);

  if (!data || !data.listings || data.listings.length === 0) return null;

  const isPersonalised = data.reason === 'personalised';
  const eyebrow = isPersonalised ? 'For your taste' : 'You might also like';
  const heading = isPersonalised
    ? `More homes that match your taste${data.reactions_seen ? ` (based on ${data.reactions_seen} reaction${data.reactions_seen === 1 ? '' : 's'})` : ''}`
    : 'Other homes like this one';

  return (
    <section
      className="bg-gradient-to-br from-[#F5EFFF] via-white to-[#FFF7F0] border-t border-gray-100 py-14"
      data-testid="similar-homes-rail"
      data-reason={data.reason}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold mb-1 ${
              isPersonalised ? 'text-[#7042cc]' : 'text-amber-700'
            }`}
          >
            {isPersonalised ? <Heart className="h-3 w-3 fill-[#7042cc]" /> : <Compass className="h-3 w-3" />}
            {eyebrow}
          </div>
          <h2
            className="text-2xl sm:text-3xl text-gray-900"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
          >
            {heading}
          </h2>
          <BudgetPill budgetWindow={data.budget_window} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.listings.map((p) => (
            <PropertyCard key={p._id} property={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SimilarHomesRail;
